import React, { useEffect, useMemo, useState } from 'react';
import { scrollToAnchor, normalizeExternalUrl } from '../utils/defaults';
import { resolvePublicPath } from '../utils/commerce';
import { jardinsCtaLabelHtml, replaceJardinsDecorInHtml } from '../utils/jardinsPicto';
import { AmbientMusicMenuControl } from './SiteAmbientMusic';

function navItemsFromSection(section) {
  const comps = section?.components || [];
  const brand = comps.find((c) => c.props?.kitStyle === 'jardins-brand');
  const links = comps.filter((c) => c.props?.kitStyle === 'jardins-nav-link');
  const cta = comps.find((c) => c.props?.kitStyle === 'jardins-nav-cta');
  return { brand, links, cta };
}

export default function JardinsCompactHeader({
  section,
  interactive,
  checkoutSlug,
  editorMode,
}) {
  const [open, setOpen] = useState(false);
  const { brand, links, cta } = useMemo(() => navItemsFromSection(section), [section]);

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleNav = (comp) => {
    if (!interactive) return;
    const p = comp.props || {};
    if (p.linkType === 'section' && p.sectionAnchor) {
      scrollToAnchor(p.sectionAnchor);
      close();
      return;
    }
    if (p.linkType === 'publicPath' && p.publicPath && checkoutSlug) {
      window.location.href = resolvePublicPath(checkoutSlug, p.publicPath);
      close();
      return;
    }
    const url = normalizeExternalUrl(p.url);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    close();
  };

  const brandClick = (e) => {
    if (!editorMode?.onSelect || !brand) return;
    e.preventDefault();
    editorMode.onSelect(brand.id, section.id);
  };

  return (
    <header className={`wc-jardins-compact-header${open ? ' is-open' : ''}`}>
      {open ? (
        <button
          type="button"
          className="wc-jardins-compact-header__backdrop"
          aria-label="Fermer le menu"
          onClick={close}
        />
      ) : null}
      <div className="wc-jardins-compact-header__bar">
        <a
          href="#accueil"
          className="wc-jardins-compact-header__brand"
          onClick={brandClick}
        >
          {brand?.props?.content ? (
            <div
              className="wc-kit-text wc-kit-text--jardins-brand wc-jardins-compact-header__brand-inner"
              dangerouslySetInnerHTML={{
                __html: replaceJardinsDecorInHtml(brand.props.content),
              }}
            />
          ) : (
            <span className="wc-jardins-compact-header__brand-fallback">La table des jardins</span>
          )}
        </a>
        <div className="wc-jardins-compact-header__actions">
          <AmbientMusicMenuControl className="wc-jardins-compact-header__sound" />
          <button
            type="button"
            className="wc-jardins-compact-header__toggle"
            aria-expanded={open}
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
          </button>
        </div>
      </div>
      <nav className={`wc-jardins-compact-header__nav${open ? ' is-open' : ''}`} aria-hidden={!open}>
        {links.map((comp) => (
          <button
            key={comp.id}
            type="button"
            className="wc-jardins-compact-header__nav-link"
            onClick={() => handleNav(comp)}
          >
            {comp.props?.label || 'Lien'}
          </button>
        ))}
        {cta ? (
          <button
            type="button"
            className="wc-jardins-compact-header__nav-cta"
            onClick={() => handleNav(cta)}
          >
            <span
              className="wc-jardins-compact-header__nav-cta-label"
              dangerouslySetInnerHTML={{
                __html: jardinsCtaLabelHtml(cta.props?.label, 'Réserver'),
              }}
            />
          </button>
        ) : null}
      </nav>
    </header>
  );
}
